import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/conciliaciones")({
  component: Conciliaciones,
});

// ===========================================================================
// LAS ÚLTIMAS CONCILIACIONES, Y LAS DESHECHAS (#34).
//
// Contabilidad lo pidió dos veces con las mismas palabras. `/auditoria` existe
// y es el registro de TODO: no responde «¿qué se conció esta mañana?» sin que
// alguien sepa qué filtro poner.
//
// Dos pestañas porque son dos preguntas distintas y salen de dos sitios
// distintos: lo hecho vive en `cruce`, y lo deshecho NO —desvincular borra la
// fila—, así que sale de la bitácora, de lo que se guardó antes de borrar.
// ===========================================================================

type Fila = {
  movimientoId: string;
  momento: string;
  quien: string;
  automatico: boolean;
  boleta: string | null;
  cliente: string | null;
  importe: number;
  moneda: "PEN" | "USD";
  fecha: string | null;
  banco: string | null;
  regla: string | null;
  nota: string | null;
};

function Conciliaciones() {
  const [que, setQue] = useState<"hechas" | "deshechas">("hechas");
  const [usuario, setUsuario] = useState("");
  const [pagina, setPagina] = useState(0);
  const porPagina = 50;

  const { data, isPending } = api.conciliaciones.useQuery(
    ["conciliaciones", que, usuario, String(pagina)],
    { query: { que, usuario: usuario || undefined, pagina, porPagina } },
  );
  const d = data?.status === 200 ? data.body : null;
  const filas = (d?.filas ?? []) as Fila[];
  const paginas = d ? Math.ceil(d.total / porPagina) : 0;

  return (
    <>
      <Cabecera
        migas={["Control"]}
        titulo="Conciliaciones"
        facetas={[
          { clave: "Qué", valor: que === "hechas" ? "Hechas" : "Deshechas" },
          ...(usuario
            ? [
                {
                  clave: "Quién",
                  valor: usuario,
                  tono: "gris" as const,
                  alQuitar: () => {
                    setUsuario("");
                    setPagina(0);
                  },
                },
              ]
            : []),
        ]}
        acciones={
          <Button size="sm" variant="ghost" asChild>
            <Link to="/auditoria">Ver la auditoría completa</Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-6 border-b">
        {(
          [
            ["hechas", "Hechas"],
            ["deshechas", "Deshechas"],
          ] as const
        ).map(([v, t]) => (
          <button
            key={v}
            type="button"
            onClick={() => {
              setQue(v);
              setPagina(0);
            }}
            className={cn(
              "-mb-px border-b-2 px-1 pb-2.5 text-[13px] transition-colors",
              que === v
                ? "border-cyan-500 font-semibold text-cyan-700 dark:text-cyan-400"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}

        {d && d.usuarios.length > 0 ? (
          <label className="ml-auto flex items-center gap-1.5 pb-2 text-[12px]">
            <span className="text-muted-foreground">Quién</span>
            <select
              value={usuario}
              onChange={(e) => {
                setUsuario(e.target.value);
                setPagina(0);
              }}
              className="h-[28px] rounded-md border bg-background px-2 text-[12px] outline-none"
            >
              <option value="">Cualquiera</option>
              {(d.usuarios as string[]).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {isPending ? (
        <div className="space-y-2">
          {["a", "b", "c"].map((k) => (
            <Skeleton key={k} className="h-10" />
          ))}
        </div>
      ) : filas.length === 0 ? (
        <EmptyState
          title={
            que === "hechas"
              ? "Todavía no hay ninguna conciliación"
              : "No se ha deshecho ninguna"
          }
          message={
            que === "hechas"
              ? "Cuando el cruce ate un abono con su boleta, o alguien lo confirme a mano, aparecerá aquí."
              : "Aquí salen las que alguien quitó para rectificar, con la boleta de la que se desvincularon."
          }
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <table className="w-full text-[12.5px]">
              <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                <tr>
                  <th className="py-2 pl-6 text-left font-medium">Cuándo</th>
                  <th className="py-2 pl-4 text-left font-medium">Quién</th>
                  <th className="py-2 pl-4 text-left font-medium">Boleta</th>
                  <th className="py-2 pl-4 text-left font-medium">Cliente</th>
                  <th className="py-2 pr-6 pl-4 text-right font-medium">
                    Importe
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr
                    key={`${f.movimientoId}-${f.momento}-${f.boleta}`}
                    className="border-b last:border-0 hover:bg-muted/40"
                  >
                    <td className="whitespace-nowrap py-2.5 pl-6 tabular-nums">
                      {f.momento}
                    </td>
                    <td className="py-2.5 pl-4">
                      {/* Quién lo ató. 190 de 204 los ató el motor y no tienen
                          firma: decir «—» los haría parecer huérfanos. */}
                      {f.automatico ? (
                        <span className="text-muted-foreground">{f.quien}</span>
                      ) : (
                        f.quien
                      )}
                    </td>
                    <td className="py-2.5 pl-4">
                      <Link
                        to="/movimiento/$id"
                        params={{ id: f.movimientoId }}
                        className="hover:underline"
                      >
                        {f.boleta ?? "—"}
                      </Link>
                      {f.nota ? (
                        <div className="text-[11px] text-muted-foreground">
                          {f.nota}
                        </div>
                      ) : null}
                    </td>
                    <td
                      className="max-w-[18rem] truncate py-2.5 pl-4"
                      title={f.cliente ?? ""}
                    >
                      {f.cliente ?? "—"}
                    </td>
                    <td className="py-2.5 pr-6 pl-4 text-right font-semibold tabular-nums">
                      {dinero(f.importe, f.moneda)}
                      {f.banco ? (
                        <div className="font-normal text-[11px] text-muted-foreground">
                          {f.banco}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {d && paginas > 1 ? (
        <div className="flex items-center justify-between gap-4 border-t pt-4">
          <p className="text-muted-foreground text-sm">
            {d.total.toLocaleString("es-PE")}{" "}
            {que === "hechas" ? "conciliaciones" : "deshechas"} · página{" "}
            {pagina + 1} de {paginas}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina === 0}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina + 1 >= paginas}
              onClick={() => setPagina((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function dinero(centimos: number, moneda: "PEN" | "USD"): string {
  const signo = moneda === "USD" ? "US$" : "S/";
  return `${signo} ${(centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
